import { useState, useEffect } from "react";
import Icon from "../AppIcon";
import Button from "./Button";
import AppImage from "../AppImage";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("inicio");

  const navigationItems = [
    { id: "inicio", label: "Início", href: "#inicio" },
    { id: "servicos", label: "Serviços", href: "#servicos" },
    { id: "depoimentos", label: "Depoimentos", href: "#depoimentos" },
    { id: "sobre", label: "Sobre Nós", href: "#sobre" },
    { id: "como-funciona", label: "Como Funciona", href: "#como-funciona" },
    { id: "contato", label: "Contato", href: "#contato" },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);

      // Update active section based on scroll position
      const sections = navigationItems?.map((item) => item?.id);
      const currentSection = sections?.find((section) => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element?.getBoundingClientRect();
          return rect?.top <= 100 && rect?.bottom >= 100;
        }
        return false;
      });

      if (currentSection) {
        setActiveSection(currentSection);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (href: string) => {
    const element = document.querySelector(href);
    if (element) {
      element?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    setIsMobileMenuOpen(false);
  };

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent(
      "Olá! Gostaria de agendar uma consulta financeira gratuita.",
    );
    window.open(`https://wa.me/553397070278?text=${message}`, "_blank");
  };

  return (
    <>
      {/* Trust Badge Strip */}
      <div className="bg-primary text-primary-foreground py-2 px-4 text-center text-sm font-medium">
        <div className="max-w-7xl mx-auto flex items-center justify-center space-x-6">
          <span className="flex items-center space-x-2">
            <Icon name="Shield" size={16} />
            <span>Certificado CNPJ: 63.284.659/0001-37</span>
          </span>
          <span className="hidden md:flex items-center space-x-2">
            <Icon name="Users" size={16} />
            <span>+15.000 clientes atendidos</span>
          </span>
          <span className="hidden lg:flex items-center space-x-2">
            <Icon name="Award" size={16} />
            <span>Soluções 100% transparentes</span>
          </span>
        </div>
      </div>
      {/* Main Header */}
      <header
        className={`sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 transition-smooth ${
          isScrolled ? "shadow-subtle" : ""
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            {/* Logo */}
            <div className="flex items-center">
              <a
                href="#inicio"
                onClick={(e) => {
                  e?.preventDefault();
                  handleNavClick("#inicio");
                }}
                className="flex items-center space-x-3 transition-smooth hover:opacity-80"
              >
                <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                  <AppImage
                    src="assets/images/LOGO_ISOLADA_WHITE.png"
                    alt="TJEP Soluções Financeiras Logo"
                    className="w-6 h-6"
                  />
                </div>
                <div className="hidden sm:block">
                  <h1 className="text-xl font-bold font-heading text-foreground">
                    TJEP
                  </h1>
                  <p className="text-xs text-muted-foreground font-medium">
                    Soluções Financeiras
                  </p>
                </div>
              </a>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8">
              {navigationItems?.map((item) => (
                <a
                  key={item?.id}
                  href={item?.href}
                  onClick={(e) => {
                    e?.preventDefault();
                    handleNavClick(item?.href);
                  }}
                  className={`text-sm font-medium transition-smooth hover:text-primary ${
                    activeSection === item?.id
                      ? "text-primary border-b-2 border-primary pb-1"
                      : "text-foreground"
                  }`}
                >
                  {item?.label}
                </a>
              ))}
            </nav>

            {/* Desktop CTA */}
            <div className="hidden lg:flex items-center space-x-4">
              <Button
                variant="outline"
                size="sm"
                onClick={handleWhatsAppClick}
                iconName="MessageCircle"
                iconPosition="left"
                className="transition-smooth hover:shadow-cta"
              >
                Consulta Grátis
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <div className="lg:hidden">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                iconName={isMobileMenuOpen ? "X" : "Menu"}
              />
            </div>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden border-t bg-background/95 backdrop-blur">
            <div className="px-4 py-4 space-y-3">
              {navigationItems?.map((item) => (
                <a
                  key={item?.id}
                  href={item?.href}
                  onClick={(e) => {
                    e?.preventDefault();
                    handleNavClick(item?.href);
                  }}
                  className={`block px-3 py-2 text-base font-medium rounded-md transition-smooth ${
                    activeSection === item?.id
                      ? "bg-primary/10 text-primary"
                      : "text-foreground hover:bg-muted hover:text-primary"
                  }`}
                >
                  {item?.label}
                </a>
              ))}
              <div className="pt-3 border-t">
                <Button
                  variant="default"
                  fullWidth
                  onClick={handleWhatsAppClick}
                  iconName="MessageCircle"
                  iconPosition="left"
                >
                  Consulta Grátis via WhatsApp
                </Button>
              </div>
            </div>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;
